/**
 * 承認ダイアログ.
 * (C)2024 Chubu Electric Power Grid Co.,Inc. All Rights Reserved.
 * version 1.00 NEC 2024/12/31 初版
 */
import React from 'react';
import PropTypes from 'prop-types';
import {Field} from 'redux-form';

import Grid from '@mui/material/Grid';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';

import {PositiveButton} from '../atoms/Buttons/PositiveButton.js';
import {DangerButton} from '../atoms/Buttons/DangerButton.js';
import {renderTextField} from '../atoms/CustomPart.js';
import * as validateRule from '../validateRule.js';
import {requiredCheck, lengthCheck} from '../../common/inputCheck.js';
import CheckCircle from '@mui/icons-material/CheckCircle';
import Cancel from '@mui/icons-material/Cancel';

/**
 * 承認ダイアログ
 * @param {object} props プロパティ
 * @param {func} approve 承認処理
 * @param {func} sasimodosi 差戻処理
 * @param {object} settings 各部品のスタイル
 *        {comment: {disabled: true}, approval: {style: {display: 'none'}}, sasimodosi: {style: {display: 'none'}}}
 * @return {*} 承認ダイアログ
 */
const Approval = ({props, approve, sasimodosi, settings=null}) => {
  const ref = React.useRef(null);
  const getSize = () => {
    let headerHeight = document.getElementById('mainHeader') ? document.getElementById('mainHeader').clientHeight : 60;
    let boxWidth = document.getElementById('mainContent') ? document.getElementById('mainContent').clientWidth - 40 : 800;
    return {top: headerHeight, width: boxWidth};
  };
  const [size, setSize] = React.useState(getSize());
  React.useEffect(() => {
    const callback = function(mutationsList, observer) {
      for (const mutation of mutationsList) {
        if (mutation.type === 'childList') {
          if (mutation.target.id == 'mainDiv' && mutation.removedNodes.length > 0) {
            setSize(getSize());
          }
        }
      }
    };
    const targetNode = document.getElementById('mainDiv');
    const observer = new MutationObserver(callback);
    observer.observe(targetNode, {childList: true, subtree: true});
    const handleWindowResize = () => {
      setSize(getSize());
    };
    window.addEventListener('resize', handleWindowResize);
    return () => {
      window.removeEventListener('resize', handleWindowResize);
      observer.disconnect();
    };
  });
  let commentDisabled = false;
  let approvalStyle = {};
  let sasimodosiStyle = {};
  if (settings?.comment) {
    commentDisabled = !!settings.comment.disabled;
  }
  if (settings?.approval) {
    approvalStyle = settings.approval.style || {};
  }
  if (settings?.sasimodosi) {
    sasimodosiStyle = settings.sasimodosi.style || {};
  }
  return (
    <Box component="div" sx={{marginBottom: '220px'}} >
      <Box id="approval-floating" ref={ref} component="div"
        sx={{position: 'fixed', zIndex: 1000, width: size.width, top: size.top}} >
        <Accordion defaultExpanded={true} expanded={true}>
          <AccordionSummary
            aria-controls="panel1a-content"
            id="approval-header">
            <Typography>承認</Typography>
          </AccordionSummary>
          <AccordionDetails style={{padding: '8px 16px 8px', overflow: 'hidden'}}>
            <Grid
              container
              justifyContent="flex-start"
              alignItems="flex-start"
              spacing={2}>
              <Grid key="approvalCommentGrid" item xs={8}>
                <Field
                  id="approvalCommentField"
                  name="approvalComment"
                  component={renderTextField}
                  className="bf-form-field"
                  label='コメント'
                  variant="outlined"
                  type="text"
                  fullWidth
                  multiline
                  InputProps={{
                    readOnly: false,
                  }}
                  rows={4}
                  required={true}
                  validate={[
                    validateRule.maxLength(255),
                  ]}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  disabled={commentDisabled}
                />
              </Grid>
              <Grid item xs={4}>
                <Grid
                  container
                  justifyContent="flex-start"
                  alignItems="flex-start"
                  direction="column"
                  spacing={1}>
                  <Grid key="approvalButtonGrid" item>
                    <PositiveButton
                      id="approvalButton"
                      variant="contained"
                      onClick={() => {
                        const {values, doShowMessage} = props;
                        let result = lengthCheck('コメント', values['approvalComment'], 255);
                        if (result.error) {
                          doShowMessage({
                            message: result.message,
                          });
                          return;
                        }
                        if (!approve) {
                          return;
                        }
                        approve();
                      }}
                      disabled={false}
                      startIcon={<CheckCircle />}
                      // size="large"
                      style={approvalStyle}>
                      <span>承認</span>
                    </PositiveButton>
                  </Grid>
                  <Grid key="returnButtonGrid" item>
                    <DangerButton
                      id="returnButton"
                      variant="contained"
                      onClick={() => {
                        const {values, doShowMessage} = props;
                        let result = requiredCheck('コメント', values['approvalComment']);
                        if (result.error) {
                          doShowMessage({
                            message: result.message,
                          });
                          return;
                        }
                        result = lengthCheck('コメント', values['approvalComment'], 255);
                        if (result.error) {
                          doShowMessage({
                            message: result.message,
                          });
                          return;
                        }

                        if (!sasimodosi) {
                          return;
                        }
                        sasimodosi();
                      }}
                      disabled={false}
                      startIcon={<Cancel />}
                      // size="large"
                      style={sasimodosiStyle}>
                      <span>差戻</span>
                    </DangerButton>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
      </Box>
    </Box>
  );
};

Approval.propTypes = {
  props: PropTypes.object,
  values: PropTypes.object,
  approve: PropTypes.func,
  sasimodosi: PropTypes.func,
  settings: PropTypes.object,
  doShowMessage: PropTypes.func,
};

export default Approval;
